import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, message } from 'antd';
import CanvasDraw from 'react-canvas-draw';
import api from '../../../../../services/api';
import { COMPLETE_STATUS } from '../constants';
import { useDispatch } from 'react-redux';
import { setState } from '../../../../../redux/pages/training-items/actions';
import actions from '../../../../../redux/user/actions';

export const SignatureModal = props => {
  const dispatch = useDispatch();
  const { isVisible, setIsVisible, userID, onSaveTrainingItem, setSignatureUrl } = props;
  const canvasRef = useRef(null);
  const [canvasKey, setCanvasKey] = useState(Date.now());

  const dataURLtoBlob = (dataURL: string) => {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL
      .split(',')[0]
      .split(':')[1]
      .split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const updateUser = async () => {
    try {
      const response = await api.get(`/v1/users/${userID}`);
      if (response.status === 200) {
        dispatch({
          type: actions.SET_USER,
          payload: response?.data?.Data
        })
      }
    }catch (e) {
      console.log('Failed to update user', e);
    }
  }

  const saveSignature = async (signatureFile: File) => {
    try {
      const form = new FormData();
      form.append('signature', signatureFile);
      const res = await api.post(`/v1/users/${userID}/signature`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.status === 200) {
        setIsVisible(false);
        if (onSaveTrainingItem) {
          dispatch(setState({ selectedTrackedItemStatus: COMPLETE_STATUS  }));
          await onSaveTrainingItem()
        }
        if (setSignatureUrl) {
          setSignatureUrl(res.data.Data);
        }
        await updateUser();
      }
    } catch (e) {
      console.log('Failed to save profile signature', e);
    }
  };

  const handleOk = async () => {
    const saveData = canvasRef.current.getSaveData();

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const { width, height, lines } = JSON.parse(saveData);
    if (lines.length === 0) {
      message.warning('The canvas is empty. Please draw your signature before saving.');
      return;
    }

    const minWidth = 100;
    const minHeight = 100;
    canvas.width = Math.max(width || minWidth, minWidth);
    canvas.height = Math.max(height || minHeight, minHeight);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    lines.forEach(line => {
      const { points, brushColor, brushRadius } = line;

      ctx.strokeStyle = brushColor;
      ctx.lineWidth = brushRadius * 2;
      ctx.lineCap = 'round';

      for (let i = 1; i < points.length; i++) {
        const { x: x1, y: y1 } = points[i - 1];
        const { x: x2, y: y2 } = points[i];

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }
    });
    const pngUrl = canvas.toDataURL('image/png');

    const signatureBlob = dataURLtoBlob(pngUrl);

    const signatureFile = new File([signatureBlob], 'signature.png', { type: 'image/png' });

    await saveSignature(signatureFile);
  };

  const handleClear = () => {
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
  };

  useEffect(() => {
    if (isVisible && canvasRef.current) {
      setCanvasKey(Date.now());
    }
  }, [isVisible]);

  const handleCancel = () => {
    setIsVisible(false);
  };

  return (
    <>
      <Modal
        title="Draw your signature"
        open={isVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
        getContainer={false}
        okText="Save Signature"
        width={800}
        zIndex={2000}
        footer={[
          <Button key="clear" onClick={handleClear}>
            Clear
          </Button>,
          <Button key="submit" type="primary" onClick={handleOk}>
            Save Signature
          </Button>
        ]}
      >
        <CanvasDraw
          key={canvasKey}
          ref={canvasRef}
          canvasWidth={700}
          canvasHeight={400}
          brushRadius={2}
          lazyRadius={0}
        />
      </Modal>
    </>
  );
};
